/**
 * 請求先住所 / 納品先住所のパネル。
 *
 * 請求先: 登録済みの住所 / 請求先住所を編集 / 請求先住所を新規追加
 * 納品先: (あれば) 登録済みの住所 / 請求先住所と同じ / 納品先住所を編集 / 納品先住所を新規追加
 * 編集・新規追加した住所は顧客情報 (mail_contacts) にも保存する (onSave)。
 */
import { useState, type ReactNode } from 'react';

import { CommitInput } from './CommitInput';
import {
  DELIVERY_SAME_AS_BILLING,
  addressSummary,
  newAddressId,
  type AddressEntry,
  type AddressKind,
} from './model';

const EDIT = '__EDIT__';
const NEW = '__NEW__';

const FIELDS: Array<{
  key: keyof AddressEntry & string;
  label: string;
  placeholder: string;
  wide?: boolean;
}> = [
  { key: 'label', label: 'ラベル', placeholder: '例: 京都営業所' },
  { key: 'recipientName', label: '宛名', placeholder: '未入力' },
  { key: 'postalCode', label: '郵便番号', placeholder: '未入力' },
  { key: 'prefecture', label: '都道府県', placeholder: '未入力' },
  { key: 'address', label: '住所', placeholder: '未入力', wide: true },
  { key: 'buildingName', label: '建物名', placeholder: '未入力' },
  { key: 'phone', label: '電話番号', placeholder: '未入力' },
];

interface Props {
  kind: AddressKind;
  list: AddressEntry[];
  /** 選ばれている住所の ID / DELIVERY_SAME_AS_BILLING。見積書に保存済みの住所が一覧に無いときは null */
  selectedValue: string | null;
  /** 見積書に保存済みで、顧客の一覧には無い住所の表示 (下書きの編集時) */
  savedElsewhere: string | null;
  /** 顧客情報の住所を書き換えられるか (owner / editor のみ。firestore.rules と同じ) */
  canEdit: boolean;
  isLoading: boolean;
  onSelect: (value: string) => void;
  /** 住所を顧客情報に保存し、この見積書の宛先にする */
  onSave: (entry: AddressEntry) => Promise<void>;
}

const emptyEntry = (): AddressEntry => ({
  id: '',
  label: '',
  recipientName: '',
  postalCode: '',
  prefecture: '',
  address: '',
  buildingName: '',
  phone: '',
  isDefault: false,
});

export function AddressPanel({
  kind,
  list,
  selectedValue,
  savedElsewhere,
  canEdit,
  isLoading,
  onSelect,
  onSave,
}: Props) {
  const isDelivery = kind === 'delivery';
  const word = isDelivery ? '納品先住所' : '請求先住所';
  const [mode, setMode] = useState<'pick' | 'edit' | 'new'>('pick');
  /** 編集中の住所。新規追加で最初の値を確定するまでは id が空 */
  const [draft, setDraft] = useState<AddressEntry | null>(null);

  const checked = mode === 'edit' ? EDIT : mode === 'new' ? NEW : selectedValue;

  const startEdit = (targetId?: string) => {
    const target =
      list.find((a) => a.id === (targetId ?? selectedValue)) ??
      list.find((a) => a.isDefault) ??
      list[0];
    if (!target) return;
    setMode('edit');
    setDraft({ ...target });
    if (target.id !== selectedValue) onSelect(target.id);
  };

  const commitField = (key: keyof AddressEntry, value: string) => {
    if (!draft) return;
    const v = value.trim();
    if (((draft[key] as string | null | undefined) ?? '') === v) return;
    // 新規追加は最初に何か入力した時点で顧客情報に追加する
    if (!draft.id && !v) return;
    const next = { ...draft, [key]: v, id: draft.id || newAddressId() };
    setDraft(next);
    void onSave(next);
  };

  const toggleDefault = (on: boolean) => {
    if (!draft) return;
    const next = { ...draft, isDefault: on };
    setDraft(next);
    if (next.id) void onSave(next);
  };

  const note =
    mode === 'edit'
      ? '入力欄を離れると、顧客情報とこの見積書の両方に保存されます。'
      : draft?.id
        ? '顧客情報に追加しました。続けて入力できます。'
        : '入力欄を離れると顧客情報に追加され、この見積書の宛先になります。';

  return (
    <>
      <p className="text-xs text-[var(--text-muted)]">
        {isDelivery
          ? '商品の発送先として記録されます。'
          : '見積書・請求書の宛先として固定保存されます。顧客の既定が後で変わっても、この見積の表示は変わりません。'}
      </p>

      {savedElsewhere && (
        <div className="rounded-md bg-[var(--bg-elevated)] border border-[var(--border)] px-3 py-2 text-xs text-[var(--text-secondary)]">
          この見積書に保存されている住所: {savedElsewhere}
        </div>
      )}

      {isLoading ? (
        <p className="text-xs text-[var(--text-muted)] italic">顧客情報を読み込み中…</p>
      ) : (
        <>
          {(!isDelivery || list.length > 0) && (
            <fieldset className="space-y-2">
              <legend className="text-xs font-semibold text-[var(--text-secondary)] mb-1.5">
                登録済みの住所
              </legend>
              {list.length === 0 ? (
                <p className="text-xs text-[var(--accent-amber)]">
                  顧客に{word}が登録されていません。
                </p>
              ) : (
                list.map((a) => (
                  <Option
                    key={a.id}
                    name={`addr-${kind}`}
                    value={a.id}
                    checked={checked === a.id}
                    title={
                      <>
                        {a.label || '（ラベルなし）'}
                        {a.isDefault && (
                          <span className="ml-1.5 text-[11px] font-semibold text-[var(--accent-amber)]">
                            ★既定
                          </span>
                        )}
                      </>
                    }
                    sub={addressSummary(a)}
                    onChange={() => {
                      setMode('pick');
                      setDraft(null);
                      onSelect(a.id);
                    }}
                  />
                ))
              )}
            </fieldset>
          )}

          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold text-[var(--text-secondary)] mb-1.5">
              {isDelivery ? 'そのほか' : '編集・追加'}
            </legend>
            {isDelivery && (
              <Option
                name={`addr-${kind}`}
                value={DELIVERY_SAME_AS_BILLING}
                checked={checked === DELIVERY_SAME_AS_BILLING}
                title="請求先住所と同じ"
                sub="書類に納品先は表示されません"
                onChange={() => {
                  setMode('pick');
                  setDraft(null);
                  onSelect(DELIVERY_SAME_AS_BILLING);
                }}
              />
            )}
            {canEdit && list.length > 0 && (
              <Option
                name={`addr-${kind}`}
                value={EDIT}
                checked={checked === EDIT}
                title={`${word}を編集`}
                sub="選んでいる住所を書き換えます"
                onChange={() => startEdit()}
              />
            )}
            {canEdit && (
              <Option
                name={`addr-${kind}`}
                value={NEW}
                checked={checked === NEW}
                title={`${word}を新規追加`}
                onChange={() => {
                  setMode('new');
                  setDraft(emptyEntry());
                }}
              />
            )}
            {!canEdit && (
              <p className="text-xs text-[var(--text-muted)]">
                顧客情報の住所を編集・追加できるのは、管理者と編集権限のある人です。
              </p>
            )}
          </fieldset>

          {mode !== 'pick' && draft && (
            <div className="space-y-3">
              {mode === 'edit' && list.length > 1 && (
                <div className="space-y-1">
                  <label
                    htmlFor={`addr-target-${kind}`}
                    className="text-xs font-medium text-[var(--text-secondary)]"
                  >
                    編集する住所
                  </label>
                  <select
                    id={`addr-target-${kind}`}
                    value={draft.id}
                    onChange={(e) => startEdit(e.target.value)}
                    className="w-full h-9 px-2 rounded-md border border-[var(--border)] bg-[var(--bg-input)] text-sm"
                  >
                    {list.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label || addressSummary(a) || '（ラベルなし）'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <p className="text-xs text-[var(--text-secondary)] rounded-md bg-[var(--accent-subtle)] px-3 py-2">
                {note}
              </p>
              {/* key で編集対象の切り替え時に入力欄を作り直す */}
              <div
                key={mode === 'edit' ? draft.id : 'new'}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {FIELDS.map((f) => (
                  <div key={f.key} className={`space-y-1 ${f.wide ? 'sm:col-span-2' : ''}`}>
                    <label
                      htmlFor={`addr-${kind}-${f.key}`}
                      className="text-xs font-medium text-[var(--text-secondary)]"
                    >
                      {f.label}
                    </label>
                    <CommitInput
                      id={`addr-${kind}-${f.key}`}
                      value={(draft[f.key] as string | null | undefined) ?? ''}
                      placeholder={f.placeholder}
                      onCommit={(t) => commitField(f.key, t)}
                      className="w-full h-9 px-2.5 rounded-md border border-[var(--border)] bg-[var(--bg-input)] text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] placeholder-shown:border-dashed placeholder-shown:border-[var(--accent-amber)] placeholder-shown:bg-[var(--accent-amber-subtle)]"
                    />
                  </div>
                ))}
              </div>
              <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isDefault === true}
                  onChange={(e) => toggleDefault(e.target.checked)}
                />
                既定の{isDelivery ? '納品先' : '請求先'}にする
              </label>
            </div>
          )}
        </>
      )}
    </>
  );
}

function Option({
  name,
  value,
  checked,
  title,
  sub,
  onChange,
}: {
  name: string;
  value: string;
  checked: boolean;
  title: ReactNode;
  sub?: string;
  onChange: () => void;
}) {
  return (
    <label
      className={`flex items-start gap-2.5 px-3 py-2.5 rounded-md border cursor-pointer transition-colors ${
        checked
          ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
          : 'border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]'
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="mt-1"
      />
      <span className="min-w-0 space-y-0.5">
        <span className="block text-[13px] font-medium text-[var(--text-primary)]">{title}</span>
        {sub && <span className="block text-xs text-[var(--text-muted)] break-words">{sub}</span>}
      </span>
    </label>
  );
}
